import React, { useState, useContext, useEffect, useRef } from "react";
import { Link as RouterLink } from "react-router-dom";
import Button from "@material-ui/core/Button";
import CssBaseline from "@material-ui/core/CssBaseline";
import TextField from "@material-ui/core/TextField";
import Link from "@material-ui/core/Link";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import ColorModeContext from "../../layout/themeContext";
import useSettings from "../../hooks/useSettings";
import IconButton from "@material-ui/core/IconButton";
import Brightness4Icon from "@material-ui/icons/Brightness4";
import Brightness7Icon from "@material-ui/icons/Brightness7";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import InputAdornment from "@material-ui/core/InputAdornment";
import { Helmet } from "react-helmet";
import BRFlag from "../../assets/brazil.png";
import USFlag from "../../assets/unitedstates.png";
import ESFlag from "../../assets/esspain.png";
import ARFlag from "../../assets/arabe.png";
import { getBackendUrl } from "../../config";
import packageJson from "../../../package.json";

const languageOptions = [
    { value: "pt-BR", label: "Português", icon: BRFlag },
    { value: "en", label: "English", icon: USFlag },
    { value: "es", label: "Spanish", icon: ESFlag },
    { value: "ar", label: "عربي", icon: ARFlag },
];

const useStyles = makeStyles((theme) => {
    const { mode } = theme;
    return {
        root: {
            width: "100vw",
            height: "100vh",
            display: "flex",
            flexDirection: "row",
            overflow: "hidden",
            position: "relative",
            [theme.breakpoints.down("sm")]: {
                flexDirection: "column",
            },
        },

        // ─── PAINEL ESQUERDO ─────────────────────────────────────────────────────
        leftPanel: {
            width: "44%",
            height: "100vh",
            background: "linear-gradient(145deg, #1a1a2e 0%, #16213e 45%, #0f3460 80%, #162447 100%)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
            overflow: "hidden",
            flexShrink: 0,
            [theme.breakpoints.down("md")]: {
                width: "40%",
            },
            [theme.breakpoints.down("sm")]: {
                width: "100%",
                height: "220px",
                flexShrink: 0,
            },
            [theme.breakpoints.down("xs")]: {
                height: "180px",
            },
        },

        // Círculos decorativos de fundo
        decorCircle1: {
            position: "absolute",
            top: "-100px",
            right: "-100px",
            width: 380,
            height: 380,
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(255,255,255,0.05) 0%, transparent 70%)",
            border: "1px solid rgba(255, 255, 255, 0.05)",
            pointerEvents: "none",
        },
        decorCircle2: {
            position: "absolute",
            bottom: "-80px",
            left: "-80px",
            width: 280,
            height: 280,
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(255,255,255,0.04) 0%, transparent 70%)",
            border: "1px solid rgba(255, 255, 255, 0.04)",
            pointerEvents: "none",
        },
        decorCircle3: {
            position: "absolute",
            top: "55%",
            right: "15%",
            width: 100,
            height: 100,
            borderRadius: "50%",
            background: "rgba(255, 255, 255, 0.03)",
            border: "1px solid rgba(255, 255, 255, 0.06)",
            animation: "$floatSlow 7s ease-in-out infinite",
            pointerEvents: "none",
        },
        decorCircle4: {
            position: "absolute",
            top: "20%",
            left: "10%",
            width: 60,
            height: 60,
            borderRadius: "50%",
            background: "rgba(255, 255, 255, 0.03)",
            border: "1px solid rgba(255, 255, 255, 0.06)",
            animation: "$floatSlow 9s ease-in-out infinite 2s",
            pointerEvents: "none",
        },
        decorLine: {
            position: "absolute",
            bottom: "30%",
            left: "0",
            right: "0",
            height: "1px",
            background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.06), transparent)",
            pointerEvents: "none",
        },

        "@keyframes floatSlow": {
            "0%, 100%": { transform: "translateY(0px) scale(1)" },
            "50%": { transform: "translateY(-18px) scale(1.04)" },
        },

        "@keyframes fadeInUp": {
            from: { opacity: 0, transform: "translateY(24px)" },
            to: { opacity: 1, transform: "translateY(0)" },
        },

        "@keyframes fadeInRight": {
            from: { opacity: 0, transform: "translateX(-20px)" },
            to: { opacity: 1, transform: "translateX(0)" },
        },

        leftContent: {
            position: "relative",
            zIndex: 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: "32px 40px",
            textAlign: "center",
            animation: "$fadeInRight 0.7s cubic-bezier(0.4, 0, 0.2, 1)",
            [theme.breakpoints.down("sm")]: {
                padding: "16px 24px",
            },
        },

        logoImgLeft: {
            width: "100%",
            maxWidth: "200px",
            height: "auto",
            maxHeight: "65px",
            margin: "0 auto 28px auto",
            filter: "drop-shadow(0 2px 12px rgba(0,0,0,0.5)) brightness(1.15)",
            content: "url(" + theme.calculatedLogoDark() + ")",
            [theme.breakpoints.down("sm")]: {
                maxWidth: "140px",
                maxHeight: "45px",
                marginBottom: "12px",
            },
        },

        tagline: {
            color: "rgba(255, 255, 255, 0.92)",
            fontWeight: 700,
            fontSize: "1.5rem",
            lineHeight: 1.3,
            marginBottom: "12px",
            letterSpacing: "-0.02em",
            [theme.breakpoints.down("sm")]: {
                fontSize: "1.1rem",
                marginBottom: "4px",
            },
            [theme.breakpoints.down("xs")]: {
                display: "none",
            },
        },

        subTagline: {
            color: "rgba(255, 255, 255, 0.45)",
            fontSize: "0.9rem",
            lineHeight: 1.7,
            maxWidth: "260px",
            marginBottom: "40px",
            [theme.breakpoints.down("sm")]: {
                display: "none",
            },
        },

        featureBullets: {
            display: "flex",
            flexDirection: "column",
            gap: "14px",
            width: "100%",
            maxWidth: "260px",
            [theme.breakpoints.down("sm")]: {
                display: "none",
            },
        },

        featureBullet: {
            display: "flex",
            alignItems: "center",
            gap: "12px",
            color: "rgba(255, 255, 255, 0.55)",
            fontSize: "0.875rem",
            fontWeight: 400,
            letterSpacing: "0.01em",
        },

        featureDot: {
            width: 7,
            height: 7,
            borderRadius: "50%",
            backgroundColor: theme.palette.primary.main,
            flexShrink: 0,
            boxShadow: `0 0 8px ${theme.palette.primary.main}80`,
        },

        // ─── PAINEL DIREITO ───────────────────────────────────────────────────────
        rightPanel: {
            flex: 1,
            height: "100vh",
            background: mode === "light" ? "#f8fafc" : "#0d1117",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "40px 32px",
            overflowY: "auto",
            position: "relative",
            [theme.breakpoints.down("sm")]: {
                flex: 1,
                minHeight: 0,
                padding: "24px 20px",
            },
        },

        topControls: {
            position: "absolute",
            top: 20,
            right: 20,
            display: "flex",
            alignItems: "center",
            gap: 6,
            zIndex: 10,
        },

        iconButton: {
            background: mode === "light" ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.06)",
            border: `1px solid ${mode === "light" ? "#e2e8f0" : "rgba(255,255,255,0.1)"}`,
            borderRadius: "10px",
            color: mode === "light" ? "#475569" : "#94a3b8",
            padding: "7px",
            transition: "all 0.2s ease",
            "&:hover": {
                background: mode === "light" ? "rgba(0,0,0,0.08)" : "rgba(255,255,255,0.1)",
                transform: "scale(1.05)",
            },
        },

        formWrapper: {
            width: "100%",
            maxWidth: "400px",
            animation: "$fadeInUp 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
        },

        formEyebrow: {
            display: "inline-flex",
            alignItems: "center",
            gap: "8px",
            background: mode === "light"
                ? `${theme.palette.primary.main}14`
                : `${theme.palette.primary.main}20`,
            color: theme.palette.primary.main,
            borderRadius: "20px",
            padding: "4px 14px",
            fontSize: "0.78rem",
            fontWeight: 600,
            letterSpacing: "0.04em",
            textTransform: "uppercase",
            marginBottom: "16px",
        },

        formTitle: {
            fontWeight: 700,
            fontSize: "1.9rem",
            color: mode === "light" ? "#0f172a" : "#f1f5f9",
            marginBottom: "8px",
            letterSpacing: "-0.03em",
            lineHeight: 1.2,
        },

        formSubtitle: {
            color: mode === "light" ? "#64748b" : "#64748b",
            fontSize: "0.925rem",
            marginBottom: "32px",
            lineHeight: 1.6,
        },

        form: {
            width: "100%",
        },

        textField: {
            "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
                backgroundColor: mode === "light" ? "#ffffff" : "rgba(255,255,255,0.04)",
                transition: "all 0.2s ease",
                "& fieldset": {
                    borderColor: mode === "light" ? "#e2e8f0" : "rgba(255,255,255,0.1)",
                    borderWidth: "1.5px",
                },
                "&:hover fieldset": {
                    borderColor: `${theme.palette.primary.main}80`,
                },
                "&.Mui-focused fieldset": {
                    borderColor: theme.palette.primary.main,
                    borderWidth: "2px",
                },
                "&.Mui-focused": {
                    boxShadow: `0 0 0 4px ${theme.palette.primary.main}14`,
                },
            },
            "& .MuiInputLabel-root": {
                color: mode === "light" ? "#94a3b8" : "#475569",
                fontWeight: 500,
                "&.Mui-focused": {
                    color: theme.palette.primary.main,
                },
            },
            "& .MuiOutlinedInput-input": {
                color: mode === "light" ? "#0f172a" : "#f1f5f9",
                "&::placeholder": {
                    color: mode === "light" ? "#cbd5e1" : "#334155",
                },
            },
            "& .MuiInputAdornment-root .MuiIconButton-root": {
                color: mode === "light" ? "#94a3b8" : "#475569",
                padding: "6px",
                borderRadius: "8px",
                "&:hover": {
                    background: mode === "light" ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.06)",
                    color: theme.palette.primary.main,
                },
            },
        },

        submit: {
            marginTop: "24px",
            marginBottom: "16px",
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark || theme.palette.primary.main} 100%)`,
            color: "#ffffff",
            borderRadius: "12px",
            padding: "14px 0",
            fontSize: "15px",
            fontWeight: 600,
            textTransform: "none",
            letterSpacing: "0.01em",
            boxShadow: `0 4px 16px ${theme.palette.primary.main}45`,
            border: "none",
            transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
            "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: `0 8px 28px ${theme.palette.primary.main}55`,
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark || theme.palette.primary.main} 100%)`,
            },
            "&:active": {
                transform: "translateY(0)",
                boxShadow: `0 2px 8px ${theme.palette.primary.main}40`,
            },
        },

        registerLink: {
            color: theme.palette.primary.main,
            textDecoration: "none",
            fontWeight: 600,
            fontSize: "0.9rem",
            transition: "all 0.2s ease",
            "&:hover": {
                color: theme.palette.primary.dark || theme.palette.primary.main,
                textDecoration: "underline",
            },
        },

        divider: {
            display: "flex",
            alignItems: "center",
            gap: "12px",
            margin: "20px 0",
            "& span": {
                flex: 1,
                height: "1px",
                background: mode === "light" ? "#e2e8f0" : "rgba(255,255,255,0.07)",
            },
            "& p": {
                color: mode === "light" ? "#94a3b8" : "#334155",
                fontSize: "0.8rem",
                fontWeight: 500,
                whiteSpace: "nowrap",
            },
        },

        footerText: {
            textAlign: "center",
            marginTop: "36px",
            color: mode === "light" ? "#cbd5e1" : "#1e293b",
            fontSize: "0.78rem",
        },

        // ─── LANGUAGE SELECTOR ────────────────────────────────────────────────────
        languageSelectorWrapper: {
            position: "relative",
        },

        languageDropdownBtn: {
            display: "flex",
            alignItems: "center",
            gap: "6px",
            background: mode === "light" ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.06)",
            border: `1px solid ${mode === "light" ? "#e2e8f0" : "rgba(255,255,255,0.1)"}`,
            borderRadius: "10px",
            color: mode === "light" ? "#475569" : "#94a3b8",
            fontSize: "13px",
            fontWeight: 500,
            padding: "7px 10px",
            cursor: "pointer",
            transition: "all 0.2s ease",
            "&:hover": {
                background: mode === "light" ? "rgba(0,0,0,0.08)" : "rgba(255,255,255,0.1)",
            },
        },

        flagIcon: {
            width: 18,
            height: 14,
            borderRadius: 2,
            objectFit: "cover",
        },

        languageOptions: {
            position: "absolute",
            top: "calc(100% + 8px)",
            right: 0,
            background: mode === "light" ? "#ffffff" : "#1e293b",
            boxShadow: "0 10px 30px rgba(0, 0, 0, 0.15)",
            borderRadius: "12px",
            border: `1px solid ${mode === "light" ? "#e2e8f0" : "rgba(255,255,255,0.1)"}`,
            padding: "6px",
            zIndex: 1000,
            minWidth: "150px",
        },

        languageOption: {
            background: "none",
            border: "none",
            color: mode === "light" ? "#374151" : "#e2e8f0",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            width: "100%",
            padding: "8px 12px",
            textAlign: "left",
            borderRadius: "8px",
            fontSize: "13px",
            fontWeight: 500,
            cursor: "pointer",
            transition: "all 0.15s ease",
            "&:hover": {
                background: `${theme.palette.primary.main}14`,
                color: theme.palette.primary.main,
            },
        },
    };
});

const Login = () => {
    const classes = useStyles();
    const theme = useTheme();
    const { colorMode } = useContext(ColorModeContext);
    const { appLogoFavicon, appName, mode } = colorMode;
    const [user, setUser] = useState({ email: "", password: "" });
    const [showPassword, setShowPassword] = useState(false);
    const [allowSignup, setAllowSignup] = useState(false);
    const { getPublicSetting } = useSettings();
    const { handleLogin } = useContext(AuthContext);

    const [open, setOpen] = useState(false);
    const ref = useRef();
    const [enabledLanguages, setEnabledLanguages] = useState(["pt-BR", "en"]);

    const getCompanyIdFromUrl = () => {
        const urlParams = new URLSearchParams(window.location.search);
        const companyId = urlParams.get("companyId");
        return companyId ? parseInt(companyId) : null;
    };

    const handleChangeInput = (e) => {
        setUser({ ...user, [e.target.name]: e.target.value });
    };

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    const handlSubmit = (e) => {
        e.preventDefault();
        handleLogin(user);
    };

    useEffect(() => {
        const companyId = getCompanyIdFromUrl();

        getPublicSetting("userCreation", companyId)
            .then((data) => setAllowSignup(data === "enabled"))
            .catch(() => {});

        getPublicSetting("enabledLanguages", companyId)
            .then((langs) => {
                let arr = ["pt-BR", "en"];
                try { if (langs) arr = JSON.parse(langs); } catch {}
                setEnabledLanguages(arr);
            })
            .catch(() => setEnabledLanguages(["pt-BR", "en"]));
    }, []);

    // Fecha dropdown ao clicar fora
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const current = languageOptions.find((opt) => opt.value === i18n.language) || languageOptions[0];

    const handleSelect = (opt) => {
        i18n.changeLanguage(opt.value);
        localStorage.setItem("language", opt.value);
        setOpen(false);
        window.location.reload();
    };

    return (
        <>
            <Helmet>
                <title>{appName || "Quero Atendimento"}</title>
                <link rel="icon" href={appLogoFavicon || "/default-favicon.ico"} />
            </Helmet>

            <CssBaseline />

            <div className={classes.root}>

                {/* ─── PAINEL ESQUERDO — BRANDING ─────────────────────────────── */}
                <div className={classes.leftPanel}>
                    <div className={classes.decorCircle1} />
                    <div className={classes.decorCircle2} />
                    <div className={classes.decorCircle3} />
                    <div className={classes.decorCircle4} />
                    <div className={classes.decorLine} />

                    <div className={classes.leftContent}>
                        <img className={classes.logoImgLeft} alt="logo" />

                        <Typography className={classes.tagline}>
                            Atendimento inteligente<br />para sua equipe
                        </Typography>

                        <Typography className={classes.subTagline}>
                            Gerencie conversas, automatize processos e entregue experiências incríveis para seus clientes.
                        </Typography>

                        <div className={classes.featureBullets}>
                            {[
                                "Atendimento multicanal integrado",
                                "Automação com chatbots e IA",
                                "Relatórios e métricas em tempo real",
                                "Gestão de filas e equipes",
                            ].map((feat) => (
                                <div key={feat} className={classes.featureBullet}>
                                    <div className={classes.featureDot} />
                                    {feat}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* ─── PAINEL DIREITO — FORMULÁRIO ─────────────────────────────── */}
                <div className={classes.rightPanel}>

                    {/* Controles: idioma + tema */}
                    <div className={classes.topControls}>
                        <div ref={ref} className={classes.languageSelectorWrapper}>
                            <button
                                onClick={() => setOpen((o) => !o)}
                                className={classes.languageDropdownBtn}
                            >
                                <img src={current.icon} alt={current.label} className={classes.flagIcon} />
                                <span>{current.label}</span>
                                <span style={{ fontSize: "10px", opacity: 0.6 }}>▾</span>
                            </button>

                            {open && (
                                <div className={classes.languageOptions}>
                                    {languageOptions
                                        .filter((opt) => enabledLanguages.includes(opt.value))
                                        .map((opt) => (
                                            <button
                                                key={opt.value}
                                                onClick={() => handleSelect(opt)}
                                                className={classes.languageOption}
                                            >
                                                <img src={opt.icon} alt={opt.label} className={classes.flagIcon} />
                                                {opt.label}
                                            </button>
                                        ))}
                                </div>
                            )}
                        </div>

                        <IconButton
                            className={classes.iconButton}
                            onClick={colorMode.toggleColorMode}
                            size="small"
                        >
                            {mode === "dark" ? (
                                <Brightness7Icon fontSize="small" />
                            ) : (
                                <Brightness4Icon fontSize="small" />
                            )}
                        </IconButton>
                    </div>

                    {/* Formulário */}
                    <div className={classes.formWrapper}>
                        <div className={classes.formEyebrow}>
                            Acesso ao sistema
                        </div>

                        <Typography className={classes.formTitle}>
                            Bem-vindo de volta
                        </Typography>

                        <Typography className={classes.formSubtitle}>
                            Entre com suas credenciais para acessar o painel
                        </Typography>

                        <form className={classes.form} noValidate onSubmit={handlSubmit}>
                            <TextField
                                variant="outlined"
                                margin="normal"
                                required
                                fullWidth
                                id="email"
                                label={i18n.t("login.form.email")}
                                name="email"
                                value={user.email}
                                onChange={handleChangeInput}
                                autoComplete="email"
                                autoFocus
                                className={classes.textField}
                            />
                            <TextField
                                variant="outlined"
                                margin="normal"
                                required
                                fullWidth
                                name="password"
                                label={i18n.t("login.form.password")}
                                type={showPassword ? "text" : "password"}
                                id="password"
                                value={user.password}
                                onChange={handleChangeInput}
                                autoComplete="current-password"
                                className={classes.textField}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label="toggle password visibility"
                                                onClick={togglePasswordVisibility}
                                                edge="end"
                                                size="small"
                                            >
                                                {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />

                            <Button
                                type="submit"
                                fullWidth
                                variant="contained"
                                color="primary"
                                className={classes.submit}
                            >
                                {i18n.t("login.buttons.submit")}
                            </Button>

                            {allowSignup && (
                                <Grid container justifyContent="center">
                                    <Grid item>
                                        <Link
                                            href="#"
                                            variant="body2"
                                            component={RouterLink}
                                            to="/signup"
                                            className={classes.registerLink}
                                        >
                                            {i18n.t("login.buttons.register")}
                                        </Link>
                                    </Grid>
                                </Grid>
                            )}
                        </form>

                        <Typography className={classes.footerText}>
                            Desenvolvido por GoldTarget.com.br &nbsp;·&nbsp; v{packageJson.version}
                        </Typography>
                    </div>
                </div>

            </div>
        </>
    );
};

export default Login;
